import React, { useState, useEffect, useRef } from 'react';
import { Pause, RotateCcw } from 'lucide-react';

const AngryBirds = ({ setgameshowing }) => {
    const canvasRef = useRef(null);
    const [gameState, setGameState] = useState('ready'); // ready, aiming, flying, complete, failed
    const [score, setScore] = useState(0);
    const [birds, setBirds] = useState(3);
    const [isPaused, setIsPaused] = useState(false);
    const [stars, setStars] = useState(0);

    const gameRef = useRef({
        bird: null,
        trajectory: [],
        dragStart: null,
        structures: [],
        pigs: [],
        particles: [],
        frame: 0,
        cloudOffset: 0
    });

    const SLINGSHOT_POS = { x: 150, y: 400 };
    const MAX_DRAG = 100;
    const GRAVITY = 0.5;
    const FRICTION = 0.99;

    useEffect(() => {
        initLevel();
    }, []);

    const initLevel = () => {
        const g = gameRef.current;

        // Create structures (blocks)
        g.structures = [
            // Left tower
            { x: 500, y: 380, w: 60, h: 20, type: 'wood', hp: 3, vx: 0, vy: 0 },
            { x: 500, y: 360, w: 60, h: 20, type: 'wood', hp: 3, vx: 0, vy: 0 },
            { x: 500, y: 340, w: 60, h: 20, type: 'wood', hp: 3, vx: 0, vy: 0 },
            { x: 480, y: 320, w: 20, h: 40, type: 'stone', hp: 5, vx: 0, vy: 0 },
            { x: 540, y: 320, w: 20, h: 40, type: 'stone', hp: 5, vx: 0, vy: 0 },

            // Right tower
            { x: 700, y: 380, w: 60, h: 20, type: 'wood', hp: 3, vx: 0, vy: 0 },
            { x: 700, y: 360, w: 60, h: 20, type: 'wood', hp: 3, vx: 0, vy: 0 },
            { x: 700, y: 340, w: 60, h: 20, type: 'ice', hp: 2, vx: 0, vy: 0 },
            { x: 680, y: 320, w: 20, h: 40, type: 'stone', hp: 5, vx: 0, vy: 0 },
            { x: 740, y: 320, w: 20, h: 40, type: 'stone', hp: 5, vx: 0, vy: 0 },

            // Top platform
            { x: 600, y: 300, w: 120, h: 20, type: 'wood', hp: 3, vx: 0, vy: 0 },
        ];

        // Create pigs
        g.pigs = [
            { x: 520, y: 365, r: 15, hp: 2, vx: 0, vy: 0, helmet: false },
            { x: 720, y: 365, r: 15, hp: 2, vx: 0, vy: 0, helmet: false },
            { x: 620, y: 280, r: 18, hp: 3, vx: 0, vy: 0, helmet: true },
        ];

        g.bird = null;
        g.trajectory = [];
        g.particles = [];
        setGameState('ready');
        setScore(0);
        setBirds(3);
        setStars(0);
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let animationId;

        const gameLoop = () => {
            if (!isPaused) {
                update();
                render(ctx);
            }
            animationId = requestAnimationFrame(gameLoop);
        };

        gameLoop();

        return () => cancelAnimationFrame(animationId);
    }, [gameState, isPaused]);

    const update = () => {
        const g = gameRef.current;
        g.frame++;

        if (gameState === 'flying' && g.bird) {
            // Update bird physics
            g.bird.vy += GRAVITY;
            g.bird.vx *= FRICTION;
            g.bird.vy *= FRICTION;
            g.bird.x += g.bird.vx;
            g.bird.y += g.bird.vy;

            // Add to trajectory
            if (g.frame % 3 === 0) {
                g.trajectory.push({ x: g.bird.x, y: g.bird.y });
                if (g.trajectory.length > 20) g.trajectory.shift();
            }

            // Check collisions with structures
            g.structures.forEach((block, idx) => {
                if (checkCircleRectCollision(g.bird, block)) {
                    handleBirdBlockCollision(g.bird, block);
                    block.hp -= 1;
                    if (block.hp <= 0) {
                        createParticles(block.x, block.y, block.type);
                        g.structures.splice(idx, 1);
                        setScore(s => s + 100);
                    }
                }
            });

            // Check collisions with pigs
            g.pigs.forEach((pig, idx) => {
                if (checkCircleCollision(g.bird, pig)) {
                    pig.hp -= 1;
                    const impact = Math.sqrt(g.bird.vx ** 2 + g.bird.vy ** 2);
                    pig.vx = g.bird.vx * 0.3;
                    pig.vy = g.bird.vy * 0.3;

                    if (pig.hp <= 0) {
                        createParticles(pig.x, pig.y, 'pig');
                        g.pigs.splice(idx, 1);
                        setScore(s => s + 500);
                    }
                }
            });

            // Check if bird is out of bounds or stopped
            if (g.bird.y > 450 || g.bird.x > 900 ||
                (Math.abs(g.bird.vx) < 0.5 && Math.abs(g.bird.vy) < 0.5 && g.bird.y > 350)) {
                g.bird = null;
                g.trajectory = [];

                // Check win/lose condition
                setTimeout(() => {
                    if (g.pigs.length === 0) {
                        const bonusPoints = birds * 10000;
                        setScore(s => s + bonusPoints);
                        const finalScore = score + bonusPoints;
                        const earnedStars = finalScore > 15000 ? 3 : finalScore > 10000 ? 2 : 1;
                        setStars(earnedStars);
                        setGameState('complete');
                    } else if (birds === 0) {
                        setGameState('failed');
                    } else {
                        setGameState('ready');
                    }
                }, 500);
            }
        }

        // Update structures physics
        g.structures.forEach(block => {
            if (block.vy !== 0 || block.vx !== 0) {
                block.vy += GRAVITY * 0.5;
                block.x += block.vx;
                block.y += block.vy;
                block.vx *= 0.95;
                block.vy *= 0.95;

                // Ground collision
                if (block.y > 380) {
                    block.y = 380;
                    block.vy = 0;
                    block.vx *= 0.8;
                }
            }
        });

        // Update pigs physics
        g.pigs.forEach(pig => {
            if (pig.vy !== 0 || pig.vx !== 0) {
                pig.vy += GRAVITY * 0.5;
                pig.x += pig.vx;
                pig.y += pig.vy;
                pig.vx *= 0.95;
                pig.vy *= 0.95;

                // Ground collision
                if (pig.y + pig.r > 400) {
                    pig.y = 400 - pig.r;
                    pig.vy = 0;
                    pig.vx *= 0.8;
                }
            }
        });

        // Update particles
        g.particles = g.particles.filter(p => {
            p.life--;
            p.x += p.vx;
            p.y += p.vy;
            p.vy += 0.2;
            return p.life > 0;
        });
    };

    const render = (ctx) => {
        const g = gameRef.current;

        // Clear canvas
        ctx.fillStyle = '#87CEEB';
        ctx.fillRect(0, 0, 900, 500);

        // Ground
        ctx.fillStyle = '#8B7355';
        ctx.fillRect(0, 400, 900, 100);
        ctx.fillStyle = '#90EE90';
        ctx.fillRect(0, 390, 900, 10);

        // Clouds
        drawClouds(ctx);

        // Slingshot
        drawSlingshot(ctx);

        // Trajectory
        ctx.strokeStyle = 'rgba(255,255,255,0.4)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        g.trajectory.forEach((pos, i) => {
            if (i === 0) ctx.moveTo(pos.x, pos.y);
            else ctx.lineTo(pos.x, pos.y);
        });
        ctx.stroke();

        // Structures
        g.structures.forEach(block => {
            drawBlock(ctx, block);
        });

        // Pigs
        g.pigs.forEach(pig => {
            drawPig(ctx, pig);
        });

        // Bird
        if (g.bird) {
            drawBird(ctx, g.bird);
        } else if (gameState === 'ready' || gameState === 'aiming') {
            drawBird(ctx, { x: SLINGSHOT_POS.x, y: SLINGSHOT_POS.y - 20, r: 15 });
        }

        // Particles
        g.particles.forEach(p => {
            ctx.fillStyle = p.color;
            ctx.fillRect(p.x, p.y, 3, 3);
        });

        // Drag line
        if (gameState === 'aiming' && g.dragStart) {
            ctx.strokeStyle = '#8B4513';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(SLINGSHOT_POS.x, SLINGSHOT_POS.y);
            ctx.lineTo(g.dragStart.x, g.dragStart.y);
            ctx.stroke();
        }
    };

    const drawClouds = (ctx) => {
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        [{ x: 100, y: 80 }, { x: 300, y: 60 }, { x: 600, y: 100 }].forEach(pos => {
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, 30, 0, Math.PI * 2);
            ctx.arc(pos.x + 25, pos.y, 35, 0, Math.PI * 2);
            ctx.arc(pos.x + 50, pos.y, 30, 0, Math.PI * 2);
            ctx.fill();
        });
    };

    const drawSlingshot = (ctx) => {
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(SLINGSHOT_POS.x - 5, SLINGSHOT_POS.y, 10, 50);
        ctx.beginPath();
        ctx.arc(SLINGSHOT_POS.x, SLINGSHOT_POS.y, 8, 0, Math.PI * 2);
        ctx.fill();
    };

    const drawBird = (ctx, bird) => {
        const r = bird.r || 15;

        // Main body - darker red
        ctx.fillStyle = '#C8102E';
        ctx.beginPath();
        ctx.arc(bird.x, bird.y, r, 0, Math.PI * 2);
        ctx.fill();

        // Lighter red shading
        ctx.fillStyle = '#DC143C';
        ctx.beginPath();
        ctx.arc(bird.x - r * 0.3, bird.y - r * 0.2, r * 0.6, 0, Math.PI * 2);
        ctx.fill();

        // Belly/chest area (tan/beige)
        ctx.fillStyle = '#D2B48C';
        ctx.beginPath();
        ctx.ellipse(bird.x, bird.y + r * 0.3, r * 0.6, r * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();

        // Black outline
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(bird.x, bird.y, r, 0, Math.PI * 2);
        ctx.stroke();

        // Feathers on top (dark red)
        ctx.fillStyle = '#8B0000';
        ctx.beginPath();
        ctx.ellipse(bird.x - r * 0.3, bird.y - r * 0.8, r * 0.3, r * 0.5, -0.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.ellipse(bird.x + r * 0.2, bird.y - r * 0.9, r * 0.4, r * 0.6, 0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Angry eyebrows/forehead
        ctx.fillStyle = '#8B0000';
        ctx.beginPath();
        ctx.moveTo(bird.x - r * 0.6, bird.y - r * 0.3);
        ctx.lineTo(bird.x - r * 0.2, bird.y - r * 0.5);
        ctx.lineTo(bird.x - r * 0.2, bird.y - r * 0.2);
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(bird.x + r * 0.6, bird.y - r * 0.3);
        ctx.lineTo(bird.x + r * 0.2, bird.y - r * 0.5);
        ctx.lineTo(bird.x + r * 0.2, bird.y - r * 0.2);
        ctx.fill();

        // White eyes
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(bird.x - r * 0.25, bird.y - r * 0.1, r * 0.35, 0, Math.PI * 2);
        ctx.arc(bird.x + r * 0.25, bird.y - r * 0.1, r * 0.35, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = 'black';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(bird.x - r * 0.25, bird.y - r * 0.1, r * 0.35, 0, Math.PI * 2);
        ctx.arc(bird.x + r * 0.25, bird.y - r * 0.1, r * 0.35, 0, Math.PI * 2);
        ctx.stroke();

        // Black pupils
        ctx.fillStyle = 'black';
        ctx.beginPath();
        ctx.arc(bird.x - r * 0.2, bird.y - r * 0.05, r * 0.15, 0, Math.PI * 2);
        ctx.arc(bird.x + r * 0.3, bird.y - r * 0.05, r * 0.15, 0, Math.PI * 2);
        ctx.fill();

        // Beak (orange/yellow)
        ctx.fillStyle = '#FFA500';
        ctx.beginPath();
        ctx.moveTo(bird.x, bird.y + r * 0.1);
        ctx.lineTo(bird.x + r * 0.6, bird.y);
        ctx.lineTo(bird.x + r * 0.5, bird.y + r * 0.2);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = 'black';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Small tail feathers on back
        ctx.fillStyle = '#8B0000';
        ctx.beginPath();
        ctx.moveTo(bird.x - r, bird.y);
        ctx.lineTo(bird.x - r * 1.3, bird.y - r * 0.2);
        ctx.lineTo(bird.x - r * 0.9, bird.y - r * 0.1);
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(bird.x - r, bird.y);
        ctx.lineTo(bird.x - r * 1.3, bird.y + r * 0.2);
        ctx.lineTo(bird.x - r * 0.9, bird.y + r * 0.1);
        ctx.fill();
    };

    const drawBlock = (ctx, block) => {
        const colors = {
            wood: '#D2691E',
            stone: '#808080',
            ice: '#B0E0E6'
        };

        ctx.fillStyle = colors[block.type];
        ctx.fillRect(block.x - block.w / 2, block.y - block.h / 2, block.w, block.h);
        ctx.strokeStyle = 'rgba(0,0,0,0.3)';
        ctx.lineWidth = 2;
        ctx.strokeRect(block.x - block.w / 2, block.y - block.h / 2, block.w, block.h);
    };

    const drawPig = (ctx, pig) => {
        const r = pig.r;

        // Main body - bright green
        ctx.fillStyle = '#7FD13A';
        ctx.beginPath();
        ctx.arc(pig.x, pig.y, r, 0, Math.PI * 2);
        ctx.fill();

        // Black outline
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Darker green spots
        ctx.fillStyle = 'rgba(100, 170, 50, 0.4)';
        ctx.beginPath();
        ctx.arc(pig.x - r * 0.4, pig.y - r * 0.3, r * 0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(pig.x + r * 0.3, pig.y - r * 0.5, r * 0.15, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(pig.x + r * 0.4, pig.y + r * 0.2, r * 0.18, 0, Math.PI * 2);
        ctx.fill();

        // Ears on top
        ctx.fillStyle = '#7FD13A';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;

        // Left ear
        ctx.beginPath();
        ctx.arc(pig.x - r * 0.5, pig.y - r * 0.8, r * 0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Right ear
        ctx.beginPath();
        ctx.arc(pig.x + r * 0.5, pig.y - r * 0.8, r * 0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Snout (yellow-green)
        ctx.fillStyle = '#B8E986';
        ctx.beginPath();
        ctx.ellipse(pig.x, pig.y + r * 0.3, r * 0.4, r * 0.35, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Nostrils
        ctx.fillStyle = '#5A8A2E';
        ctx.beginPath();
        ctx.ellipse(pig.x - r * 0.15, pig.y + r * 0.3, r * 0.08, r * 0.12, 0, 0, Math.PI * 2);
        ctx.ellipse(pig.x + r * 0.15, pig.y + r * 0.3, r * 0.08, r * 0.12, 0, 0, Math.PI * 2);
        ctx.fill();

        // White eyes
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(pig.x - r * 0.35, pig.y - r * 0.1, r * 0.3, 0, Math.PI * 2);
        ctx.arc(pig.x + r * 0.35, pig.y - r * 0.1, r * 0.3, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = 'black';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Black pupils
        ctx.fillStyle = 'black';
        ctx.beginPath();
        ctx.arc(pig.x - r * 0.35, pig.y - r * 0.05, r * 0.15, 0, Math.PI * 2);
        ctx.arc(pig.x + r * 0.35, pig.y - r * 0.05, r * 0.15, 0, Math.PI * 2);
        ctx.fill();

        // Eyebrows (curved lines)
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(pig.x - r * 0.55, pig.y - r * 0.35);
        ctx.quadraticCurveTo(pig.x - r * 0.35, pig.y - r * 0.45, pig.x - r * 0.15, pig.y - r * 0.35);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(pig.x + r * 0.55, pig.y - r * 0.35);
        ctx.quadraticCurveTo(pig.x + r * 0.35, pig.y - r * 0.45, pig.x + r * 0.15, pig.y - r * 0.35);
        ctx.stroke();

        // Helmet (if applicable)
        if (pig.helmet) {
            ctx.fillStyle = '#4A4A4A';
            ctx.beginPath();
            ctx.arc(pig.x, pig.y - r * 0.4, r * 0.9, Math.PI, 0);
            ctx.fill();

            ctx.strokeStyle = 'black';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Helmet shine
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.beginPath();
            ctx.arc(pig.x - r * 0.3, pig.y - r * 0.6, r * 0.2, 0, Math.PI * 2);
            ctx.fill();
        }
    };

    const checkCircleRectCollision = (circle, rect) => {
        const dx = Math.abs(circle.x - rect.x);
        const dy = Math.abs(circle.y - rect.y);

        if (dx > rect.w / 2 + circle.r) return false;
        if (dy > rect.h / 2 + circle.r) return false;

        if (dx <= rect.w / 2) return true;
        if (dy <= rect.h / 2) return true;

        const cornerDist = (dx - rect.w / 2) ** 2 + (dy - rect.h / 2) ** 2;
        return cornerDist <= circle.r ** 2;
    };

    const checkCircleCollision = (c1, c2) => {
        const dx = c1.x - c2.x;
        const dy = c1.y - c2.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        return dist < (c1.r || 15) + c2.r;
    };

    const handleBirdBlockCollision = (bird, block) => {
        const impact = Math.sqrt(bird.vx ** 2 + bird.vy ** 2);
        bird.vx *= -0.3;
        bird.vy *= -0.3;
        block.vx = bird.vx * 0.5;
        block.vy = -impact * 0.3;
    };

    const createParticles = (x, y, type) => {
        const colors = {
            wood: '#D2691E',
            stone: '#808080',
            ice: '#B0E0E6',
            pig: '#90EE90'
        };

        for (let i = 0; i < 10; i++) {
            gameRef.current.particles.push({
                x, y,
                vx: (Math.random() - 0.5) * 8,
                vy: (Math.random() - 0.5) * 8 - 3,
                color: colors[type],
                life: 30
            });
        }
    };

    const handleMouseDown = (e) => {
        if (gameState !== 'ready' && gameState !== 'aiming') return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const dx = x - SLINGSHOT_POS.x;
        const dy = y - SLINGSHOT_POS.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 50) {
            setGameState('aiming');
            gameRef.current.dragStart = { x, y };
        }
    };

    const handleMouseMove = (e) => {
        if (gameState !== 'aiming') return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        gameRef.current.dragStart = { x, y };
    };

    const handleMouseUp = () => {
        if (gameState !== 'aiming') return;

        const g = gameRef.current;
        if (!g.dragStart) return;

        const dx = SLINGSHOT_POS.x - g.dragStart.x;
        const dy = SLINGSHOT_POS.y - g.dragStart.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 10) {
            const power = Math.min(dist, MAX_DRAG) / 10;
            g.bird = {
                x: SLINGSHOT_POS.x,
                y: SLINGSHOT_POS.y - 20,
                r: 15,
                vx: dx * 0.08 * power,
                vy: dy * 0.08 * power
            };

            setBirds(b => b - 1);
            setGameState('flying');
        }

        g.dragStart = null;
    };

    const handleRestart = () => {
        initLevel();
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-blue-300 to-blue-400 p-4">
            <div className="bg-white rounded-lg shadow-2xl p-6 w-full">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-3xl font-bold text-red-600">Angry Birds</h1>
                    <div className="flex items-center gap-6">
                        <div className="text-xl font-bold">Score: {score}</div>
                        <div className="flex gap-1">
                            {[...Array(birds)].map((_, i) => (
                                <div key={i} className="w-6 h-6 bg-red-500 rounded-full"></div>
                            ))}
                        </div>
                        <button
                            onClick={() => setIsPaused(!isPaused)}
                            className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                        >
                            <Pause size={20} />
                        </button>
                        <button
                            onClick={handleRestart}
                            className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                        >
                            <RotateCcw size={20} />
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <canvas
                        ref={canvasRef}
                        width={900}
                        height={500}
                        className="border-4 border-gray-800 rounded-lg cursor-crosshair"
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    />
                </div>

                {gameState === 'complete' && (
                    <div className="mt-4 p-4 bg-green-100 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-700">Level Complete!</div>
                        <div className="text-lg mt-2">
                            {'⭐'.repeat(stars)} ({stars}/3 stars)
                        </div>
                        <div className="text-lg">Final Score: {score}</div>
                        <button
                            onClick={handleRestart}
                            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                            Play Again
                        </button>
                    </div>
                )}

                {gameState === 'failed' && (
                    <div className="mt-4 p-4 bg-red-100 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-700">Level Failed!</div>
                        <div className="text-lg mt-2">Score: {score}</div>
                        <button
                            onClick={handleRestart}
                            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                <div className="flex items-center justify-between mt-4 text-sm text-gray-600 text-center">
                    <span>Click and drag the bird to aim, then release to launch!</span>
                    <button onClick={() => setgameshowing(false)} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-5xl font-bold">
                        BACK TO MAIN SITE
                    </button>
                </div>
            </div>
        </div>
    );
};
export default AngryBirds;